import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, ArrowLeft, Phone, Mail, Globe, MapPin, Save, PhoneCall, CalendarClock,
  Briefcase, UserPlus, Plus, Clock,
} from "lucide-react";
import { pretty, REL_STATUS_HEX, OPP_STAGE_HEX, hexWithAlpha, GRAY } from "./crmConstants";

const Row = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wide text-[#4B5563]">{label}</label>{children}</div>
);

export default function CrmRelationshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState({ note: "", business_days: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/relationships/${id}`);
      setD(data);
      const r = data.relationship;
      setForm({
        name: r.name || "", relationship_type: r.relationship_type || "other", status: r.status || "new",
        contact_person: r.contact_person || "", contact_role: r.contact_role || "", phone: r.phone || "",
        email: r.email || "", website: r.website || "", city: r.city || "", state_province: r.state_province || "",
        referral_source: r.referral_source || "", notes: r.notes || "",
        opened_by_user_id: r.opened_by_user_id || "", managed_by_user_id: r.managed_by_user_id || "",
        closed_by_user_id: r.closed_by_user_id || "",
        agreement_status: r.agreement_status || "not_started", agreement_notes: r.agreement_notes || "",
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load."); navigate("/crm/relationships"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/crm/relationships/${id}`, form); toast.success("Saved."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setSaving(false); }
  };
  const logContact = async () => {
    const payload = { note: log.note || undefined };
    if (log.business_days) payload.business_days = Number(log.business_days);
    try { await api.post(`/crm/relationships/${id}/log`, payload); toast.success("Contact logged."); setLog({ note: "", business_days: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  if (loading || !form) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  const r = d.relationship;
  const names = d.user_names || {};
  const staff = d.staff || [];
  const types = d.types || [];
  const statuses = d.statuses || [];
  const agreementStatuses = d.agreement_statuses || [];
  const relHex = REL_STATUS_HEX[form.status] || GRAY;

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="crm-relationship-detail">
      <button onClick={() => navigate("/crm/relationships")} className="flex items-center gap-1.5 text-[14px] text-[#1C4358] hover:underline mb-3"><ArrowLeft size={15} /> Back to relationships</button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">{r.name}</h1>
          <div className="flex items-center gap-3 flex-wrap text-[13px] text-[#4B5563] mt-1">
            <span>{pretty(r.relationship_type)}</span>
            {r.phone && <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-[#9C1E22]"><Phone size={12} /> {r.phone}</a>}
            {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1"><Mail size={12} /> {r.email}</a>}
            {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#9C1E22]"><Globe size={12} /> Website</a>}
            {(r.city || r.state_province) && <span className="flex items-center gap-1"><MapPin size={12} /> {r.city} {r.state_province}</span>}
          </div>
        </div>
        <span className="text-[12px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexWithAlpha(relHex, 0.14), color: relHex }}>{pretty(r.status)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12px] text-[#4B5563]" data-testid="rel-contributor-summary">
        <span className="font-semibold px-2 py-0.5 rounded-full bg-[#1C4358]/8 text-[#1C4358]" data-testid="rel-agreement-badge">Agreement: {pretty(r.agreement_status || "not_started")}</span>
        {names[r.opened_by_user_id] && <span>Opened by <span className="text-[#252221] font-medium">{names[r.opened_by_user_id]}</span></span>}
        {names[r.managed_by_user_id] && <span>Managed by <span className="text-[#252221] font-medium">{names[r.managed_by_user_id]}</span></span>}
        {names[r.closed_by_user_id] && <span>Closed by <span className="text-[#252221] font-medium">{names[r.closed_by_user_id]}</span></span>}
      </div>

      {/* PROMPT 12D — external referral quality / activity summary (aggregate; sensitive detail stays internal) */}
      {d.referral_quality && d.referral_quality.total_referrals > 0 && (
        <div className="mt-4 rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid="rel-referral-quality">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Referral activity</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[
              ["Total", d.referral_quality.total_referrals], ["Accepted", d.referral_quality.accepted],
              ["Completed", d.referral_quality.completed], ["Open", d.referral_quality.open],
              ["Overdue", d.referral_quality.overdue_follow_ups],
              ["Docs", d.referral_quality.documents_count], ["Meetings", d.referral_quality.meetings_count],
              ["Notes", d.referral_quality.notes_count],
            ].map(([label, val]) => (
              <div key={label}><div className="text-[11px] text-[#9AA5B1] uppercase tracking-wide">{label}</div><div className="text-[15px] font-bold text-[#1C4358] tabular-nums">{val ?? 0}</div></div>
            ))}
          </div>
          {(d.referral_quality.last_activity_date) && (
            <div className="text-[11px] text-[#9AA5B1] mt-2">Last activity {new Date(d.referral_quality.last_activity_date).toLocaleDateString()}</div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* Edit form */}
        <Card className="p-4 border-[#E6EDF2] lg:col-span-2" data-testid="rel-edit-card">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-3">Relationship details</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Name"><Input data-testid="rel-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Type">
              <Select value={form.relationship_type} onValueChange={(v) => setForm({ ...form, relationship_type: v })}>
                <SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{pretty(t)}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="rel-edit-status" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Referral source"><Input value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Contact person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Contact role"><Input value={form.contact_role} onChange={(e) => setForm({ ...form, contact_role: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="State/Province"><Input value={form.state_province} onChange={(e) => setForm({ ...form, state_province: e.target.value })} className="h-9 text-[14px]" /></Row>
            <Row label="Website"><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="h-9 text-[14px]" /></Row>
            <div />
            <Row label="Opened by">
              <Select value={form.opened_by_user_id || "none"} onValueChange={(v) => setForm({ ...form, opened_by_user_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Managed by">
              <Select value={form.managed_by_user_id || "none"} onValueChange={(v) => setForm({ ...form, managed_by_user_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Closed by">
              <Select value={form.closed_by_user_id || "none"} onValueChange={(v) => setForm({ ...form, closed_by_user_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="rel-edit-closedby" className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Agreement status">
              <Select value={form.agreement_status} onValueChange={(v) => setForm({ ...form, agreement_status: v })}>
                <SelectTrigger data-testid="rel-edit-agreement" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{agreementStatuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
          </div>
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Agreement notes <span className="normal-case text-[#9AA5B1]">(partnership paperwork — not e-signature)</span></label>
            <Textarea data-testid="rel-edit-agreement-notes" value={form.agreement_notes} onChange={(e) => setForm({ ...form, agreement_notes: e.target.value })} rows={2} className="text-[14px]" placeholder="Agreement terms, external signing link, renewal date…" />
          </div>
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Notes</label>
            <Textarea data-testid="rel-edit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-[14px]" />
          </div>
          <Button data-testid="rel-save-btn" onClick={save} disabled={saving} className="mt-3 bg-[#1C4358] hover:bg-[#152F3F] text-white gap-1">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes</Button>
        </Card>

        {/* Log contact + follow-up */}
        <Card className="p-4 border-[#E6EDF2]" data-testid="rel-log-card">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><PhoneCall size={13} /> Log contact / follow-up</div>
          <Textarea data-testid="rel-log-note" value={log.note} onChange={(e) => setLog({ ...log, note: e.target.value })} rows={2} placeholder="What happened on this call/visit?" className="text-[14px]" />
          <label className="text-[11px] text-[#4B5563] mt-2 block">Schedule follow-up (business days)</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {[3, 5, 7, 14].map((n) => (
              <button key={n} data-testid={`rel-fu-${n}`} onClick={() => setLog({ ...log, business_days: log.business_days === String(n) ? "" : String(n) })}
                className={`rounded-full px-3 h-8 text-[13px] font-medium border transition-colors ${log.business_days === String(n) ? "border-[#9C1E22] bg-[#9C1E22]/8 text-[#1C4358]" : "border-[#1C4358]/20 text-[#4B5563]"}`}>{n}d</button>
            ))}
          </div>
          <Button data-testid="rel-log-btn" onClick={logContact} className="mt-3 w-full bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><CalendarClock size={14} /> Log contact</Button>
          {r.last_contact_date && <div className="text-[11px] text-[#9AA5B1] mt-2">Last contact {(r.last_contact_date || "").slice(0, 10)}</div>}
          {(d.follow_ups || []).filter((f) => f.status === "scheduled").length > 0 && (
            <div className="mt-3 border-t border-[#F0F4F7] pt-2 space-y-1">
              <div className="text-[11px] font-bold uppercase text-[#1C4358]/70">Upcoming follow-ups</div>
              {(d.follow_ups || []).filter((f) => f.status === "scheduled").map((f) => (
                <div key={f.id} className="text-[12px] text-[#4B5563] flex items-center gap-1"><Clock size={11} /> {f.due_date} — {f.note}</div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Related opportunities */}
      <Card className="p-4 border-[#E6EDF2] mt-4" data-testid="rel-opportunities">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 flex items-center gap-1.5"><Briefcase size={13} /> Opportunities</div>
          <Button size="sm" variant="outline" data-testid="rel-new-opp" onClick={() => navigate(`/crm/opportunities?new=1&relationship_id=${id}`)} className="h-8 border-[#D6E0E7] text-[#1C4358] gap-1 text-[12px]"><Plus size={13} /> New opportunity</Button>
        </div>
        {(d.opportunities || []).length === 0 ? <p className="text-[13px] text-[#4B5563]">No opportunities linked yet.</p> : (
          <div className="divide-y divide-[#F0F4F7]">
            {d.opportunities.map((o) => {
              const hex = OPP_STAGE_HEX[o.stage] || GRAY;
              return (
                <div key={o.id} data-testid={`rel-opp-${o.id}`} onClick={() => navigate(`/crm/opportunities/${o.id}`)} className="flex items-center justify-between gap-2 py-2 cursor-pointer hover:bg-[#F5F8FA] px-1 rounded">
                  <span className="text-[14px] text-[#252221] truncate">{o.name}</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: hexWithAlpha(hex, 0.14), color: hex }}>{pretty(o.stage)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Related leads */}
      <Card className="p-4 border-[#E6EDF2] mt-4" data-testid="rel-leads">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><UserPlus size={13} /> Related leads</div>
        {(d.leads || []).length === 0 ? <p className="text-[13px] text-[#4B5563]">No leads linked to this partner yet.</p> : (
          <div className="divide-y divide-[#F0F4F7]">
            {d.leads.map((l) => (
              <div key={l.id} data-testid={`rel-lead-${l.id}`} onClick={() => navigate(`/crm/leads/${l.id}`)} className="flex items-center justify-between gap-2 py-2 cursor-pointer hover:bg-[#F5F8FA] px-1 rounded">
                <span className="text-[14px] text-[#252221] truncate">{l.name}{l.phone ? ` · ${l.phone}` : ""}</span>
                <span className="text-[11px] text-[#4B5563]">{pretty(l.pipeline_stage)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
